import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { Button, Card, Notice, Pill } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { appendAttempt, browserOf, readAttempts, setupFrom, type Attempt } from '../../lib/beacon/attempts'
import { BeaconPresentation } from '../../lib/beacon/presentations/beacon'
import { checks, comparison, headline, measures } from '../../lib/beacon/report'
import { TrialRunner, type InterruptReason, type Summary, type TrialRecord } from '../../lib/beacon/runner'
import { FrameClock, deviceContext, isFocused, isVisible } from '../../lib/beacon/timing'

/**
 * `/check-in` — Beacon, Canvas 04 §01: a three-minute attention check, optional,
 * after the resting recording and before its result. One controlled vigilance
 * task in an arcade coat; the coat (beacon/1.0) never changes a foreperiod, a
 * window or a score. Points are feedback; the four measures are the measurement,
 * worked out after the run. Practice is separate and never stored. An attempt
 * that loses the window is kept as interrupted and never stitched.
 *
 * The runner, protocol and presentation are `lib/beacon`, shared with the
 * standalone timing instrument (`prototypes/beacon`). Timing on a setup is
 * evidence from the browser until docs/beacon-measurement.md has a row for it,
 * and the result says so. Attempts are stored per user in the browser until the
 * backend has a check-in endpoint (`lib/beacon/attempts.ts`).
 */

type Phase = 'intro' | 'running' | 'done'

interface Outcome {
  summary: Summary
  interrupted: boolean
  reason: InterruptReason | null
  /** Each trial's visibility and focus at arming: the focus check's evidence (UI-QA Q2). */
  trials: Pick<TrialRecord, 'visible' | 'focused'>[]
  attempt: Attempt | null
  earlier: Attempt[]
}

export function CheckIn() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  const [params] = useSearchParams()
  const linkedSessionId = params.get('session')
  const [phase, setPhase] = useState<Phase>('intro')
  const [mode, setMode] = useState<'practice' | 'measurement'>('measurement')
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const fieldRef = useRef<HTMLDivElement | null>(null)
  const runner = useRef<TrialRunner | null>(null)
  const clock = useRef<FrameClock | null>(null)
  const presentation = useRef<BeaconPresentation | null>(null)
  const inputs = useRef(new Set<string>())
  const supported = typeof window !== 'undefined' && 'requestAnimationFrame' in window

  const finish = useCallback(
    (summary: Summary, interrupted: boolean, reason: InterruptReason | null) => {
      clock.current?.stop()
      const r = runner.current
      if (!r) return
      const stats = clock.current?.stats()
      const device = deviceContext(inputs.current, stats?.refreshHz ?? null)
      let attempt: Attempt | null = null
      let earlier: Attempt[] = []
      if (r.mode === 'measurement' && userId !== null) {
        attempt = {
          at: new Date().toISOString(),
          setup: setupFrom(device),
          protocolVersion: r.protocol.version,
          presentationVersion: r.presentation.version,
          summary,
          interrupted,
          linkedSessionId,
        }
        earlier = readAttempts(userId)
        appendAttempt(userId, attempt)
      }
      const trials = r.trials.map(({ visible, focused }) => ({ visible, focused }))
      setOutcome({ summary, interrupted, reason, trials, attempt, earlier })
      setPhase('done')
    },
    [linkedSessionId, userId],
  )

  const start = useCallback(
    (m: 'practice' | 'measurement') => {
      const field = fieldRef.current
      if (!field) return
      setMode(m)
      setPhase('running')
      const pres = new BeaconPresentation()
      pres.mount(field)
      presentation.current = pres
      const seed = Math.floor(Math.random() * 2 ** 31)
      const r = new TrialRunner({ seed, mode: m, presentation: pres.version, isVisible, isFocused })
      runner.current = r
      const fc = new FrameClock()
      clock.current = fc
      r.on('armed', () => pres.onArmed())
      r.on('lit', () => {
        pres.onLit()
        fc.afterPaint((now) => r.observed(now))
      })
      r.on('trialEnd', (t) => pres.onTrialEnd(t))
      r.on('tick', (elapsed) => pres.onTick(elapsed, r.durationMs, r.trials.length))
      r.on('ended', (s) => finish(s, false, null))
      r.on('interrupted', (reason) => {
        pres.onInterrupted(reason, r.elapsed(performance.now()))
        finish(r.summary(), true, reason)
      })
      fc.onFrame((now) => r.frame(now))
      fc.start()
      r.start(performance.now())
    },
    [finish],
  )

  useEffect(() => {
    const respond = (ev: Event) => {
      const r = runner.current
      if (!r || r.state === 'idle' || r.state === 'ended' || r.state === 'interrupted') return
      if (ev instanceof KeyboardEvent) {
        if (ev.code !== 'Space' || ev.repeat) return
        ev.preventDefault()
        inputs.current.add('keyboard')
      } else if (ev instanceof PointerEvent) {
        inputs.current.add(ev.pointerType || 'pointer')
      }
      r.respond(ev.timeStamp, performance.now())
    }
    const poke = () => runner.current?.frame(performance.now())
    document.addEventListener('keydown', respond)
    const field = fieldRef.current
    field?.addEventListener('pointerdown', respond)
    document.addEventListener('visibilitychange', poke)
    window.addEventListener('blur', poke)
    return () => {
      document.removeEventListener('keydown', respond)
      field?.removeEventListener('pointerdown', respond)
      document.removeEventListener('visibilitychange', poke)
      window.removeEventListener('blur', poke)
      clock.current?.stop()
      runner.current?.interrupt('stopped')
    }
  }, [])

  const reset = () => {
    presentation.current?.unmount()
    runner.current = null
    setOutcome(null)
    setPhase('intro')
  }

  const running = phase === 'running'
  const lengthText = mode === 'practice' ? '0:45' : '3:00'

  return (
    <div className="stack stack--standard checkin" data-phase={phase}>
      <PageHeader
        eyebrow={linkedSessionId ? 'Check-in · after your resting recording' : 'Check-in'}
        title="Beacon"
        aside={running ? <Pill tone="cyan" dot>{mode === 'practice' ? 'Practice' : 'Measurement'}</Pill> : undefined}
        actions={phase === 'intro' && (
          <Link className="btn btn--ghost btn--sm" to="/report">
            Skip today
          </Link>
        )}
        lead={
          phase === 'intro'
            ? 'A three-minute attention check. A beacon lights after an unpredictable wait — respond as soon as you see it. It is optional, and skipping never changes your EEG feedback or your baseline.'
            : running
              ? mode === 'practice'
                ? 'Practice. Nothing here is recorded in your history.'
                : 'Respond when the beacon lights. Leaving this window ends the attempt.'
              : undefined
        }
      />

      {!supported && <Notice tone="warn">This browser cannot time the task. Open BrainSpot in Chrome or Edge.</Notice>}

      <Card active className="checkin__stage">
        {/* The field is mounted always so the presentation has somewhere to draw the moment a run starts. */}
        <div ref={fieldRef} className="field field--beacon checkin__field" hidden={phase !== 'running'} aria-label="Beacon field" />
        {phase === 'intro' && (
          <div className="field field--beacon checkin__field checkin__field--idle" aria-hidden="true">
            <div className="field__hud">
              <span>BEACON</span>
              <span>{lengthText}</span>
            </div>
            <div className="beacon">
              <div className="beacon__core" />
            </div>
            <div className="field__msg">
              Respond with <span className="key">space</span> or a tap when the beacon lights
            </div>
          </div>
        )}
        {phase === 'intro' && (
          <div className="guide__actions">
            <Button variant="primary" onClick={() => start('measurement')} disabled={!supported}>
              Start the 3-minute check-in
            </Button>
            <Button variant="secondary" onClick={() => start('practice')} disabled={!supported}>
              Practise first · 0:45
            </Button>
            <span className="capture-note">Practice runs are never saved</span>
          </div>
        )}
        {running && (
          <div className="guide__actions">
            <Button variant="ghost" onClick={() => runner.current?.interrupt('stopped')}>
              Stop
            </Button>
          </div>
        )}
      </Card>

      {phase === 'intro' && (
        <Card className="span-12" eyebrow="Before you start">
          <dl className="detail-grid">
            <div className="detail-grid__item">
              <dt className="record__label">Duration</dt>
              <dd className="record__value">3:00 · about 60 signals</dd>
            </div>
            <div className="detail-grid__item">
              <dt className="record__label">Earlier attempts</dt>
              <dd className="record__value">{userId ? `${readAttempts(userId).filter((a) => a.summary.valid).length} valid on this browser` : '—'}</dd>
            </div>
            <div className="detail-grid__item">
              <dt className="record__label">Linked recording</dt>
              <dd className="record__value record__value--mono">{linkedSessionId ?? 'none'}</dd>
            </div>
            <div className="detail-grid__item">
              <dt className="record__label">This setup</dt>
              <dd className="record__value">{browserOf(navigator.userAgent)?.family ?? 'this browser'} · keyboard or tap · window {document.hasFocus() ? 'focused' : 'not focused'}</dd>
            </div>
          </dl>
          <p className="capture-note">
            Results are compared only with attempts made on the same kind of device, browser and input. A different setup starts a
            separate history rather than a corrected one. Your response times are evidence from your browser, not laboratory
            instrumentation. Leaving the tab or losing focus interrupts the attempt.
          </p>
        </Card>
      )}

      {phase === 'done' && outcome && <Result outcome={outcome} onAgain={reset} />}
    </div>
  )
}

function Result({ outcome, onAgain }: { outcome: Outcome; onAgain: () => void }) {
  const { summary: s, interrupted, reason, trials, attempt, earlier } = outcome
  const cmp = attempt ? comparison([...earlier, attempt], attempt) : null
  const tone = interrupted ? 'amber' : s.valid ? 'green' : 'amber'
  return (
    <>
      <Card eyebrow={s.mode === 'practice' ? 'Practice' : 'Your attempt'} head={<Pill tone={tone} dot className="checkin__status">{headline(s, interrupted)}</Pill>}>
        {interrupted && (
          <p className="guide__body">
            {reason === 'blur' ? 'The window lost focus.' : reason === 'hidden' ? 'The tab was hidden.' : reason === 'stale_event' ? 'An input arrived late.' : 'You stopped.'} This
            attempt stops here and is kept as interrupted. Trials are never stitched around an unrecorded break, so the next try is a fresh
            attempt.
          </p>
        )}
        {!interrupted && (
          <>
            <p className="record__label record__label--sentence" style={{ marginTop: 8 }}>
              Four measures · this attempt · worked out after the run, not during it
            </p>
            <dl className="detail-grid checkin__measures">
              {measures(s).map((m) => (
                <div className="detail-grid__item" key={m.label}>
                  <dt className="record__label">{m.label}</dt>
                  <dd className="record__value">
                    <span className="checkin__value">{m.value}</span> {m.unit}
                    <span className="checkin__sub">{m.sub}</span>
                  </dd>
                </div>
              ))}
            </dl>
            <p className="capture-note">Points shown during the run were presentation feedback and are not stored as a measure.</p>
          </>
        )}
        <p className="record__label" style={{ marginTop: 16 }}>
          Validity
        </p>
        <ul className="checkin__checks">
          {checks(s, { interrupted, reason, trials }).map((c) => (
            <li key={c.label} className={`checkin__check checkin__check--${c.state === 'not_assessed' ? 'na' : c.state}`}>
              <span aria-hidden="true">{c.state === 'ok' ? '✓' : c.state === 'warn' ? '!' : '–'}</span> {c.label}
              <span className="visually-hidden">{c.state === 'ok' ? ': passed' : c.state === 'warn' ? ': not met' : ''}</span>
              {c.state === 'not_assessed' && ' · not assessed'}
            </li>
          ))}
        </ul>
        {cmp && (
          <p className="guide__body" style={{ marginTop: 12 }}>
            {cmp.earlier === 0
              ? 'No earlier attempt on this setup and version to compare with.'
              : `Compared with ${cmp.earlier} earlier valid ${cmp.earlier === 1 ? 'attempt' : 'attempts'} on the same browser, device, input and version.`}{' '}
            {cmp.learning
              ? `Learning stage: ${cmp.daysToGo} more independent ${cmp.daysToGo === 1 ? 'day' : 'days'} before a trend is shown. Early attempts usually get faster with practice; that is learning the task, not a change in your day.`
              : ''}
          </p>
        )}
        <p className="capture-note">
          Beacon measures performance on this task, on this setup. It does not measure attention in general, fatigue, or wellbeing, and it
          is never combined with your EEG into one score. Timing on this setup is evidence from the browser until it has been checked
          against an external reference.
        </p>
      </Card>
      <div className="capture-actions">
        <Link className="btn btn--primary" to="/report">
          Back to Report
        </Link>
        <Button variant="ghost" onClick={onAgain}>
          {interrupted ? 'Start a new attempt' : 'Another attempt'}
        </Button>
      </div>
    </>
  )
}
